import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Spinner, Row, Col } from "react-bootstrap";
import Select from "react-select";
import axios from "../../context/AxiosInstance";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  FaCalendarAlt,
  FaUsers,
  FaUser,
  FaLayerGroup,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
} from "react-icons/fa";
import "../../styles/rh-modern-ui.css";
import "./CongeFormModal.css";

const MySwal = withReactContent(Swal);

const CongeFormModal = ({
  show,
  handleClose,
  isEditMode,
  selectedConge,
  token,
  refreshData,
}) => {
  const [formData, setFormData] = useState({
    id_employe: "",
    mois_conge: "",
    annee_conge: "",
    jours_pris_conge: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [employeOptions, setEmployeOptions] = useState([]);
  const [employeeStatuses, setEmployeeStatuses] = useState({});
  const [summary, setSummary] = useState({ added: 0, skipped: 0, errors: 0 });

  const [multiMonthsEnabled, setMultiMonthsEnabled] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState([]);

  const [multiEmployeesEnabled, setMultiEmployeesEnabled] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const moisOptions = [
    "Janvier",
    "Fevrier",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Aout",
    "Septembre",
    "Octobre",
    "Novembre",
    "Decembre",
  ].map((mois) => ({ value: mois, label: mois }));

  const anneeOptions = ["2024", "2025", "2026"].map((a) => ({ value: a, label: a }));

  useEffect(() => {
    if (isEditMode && selectedConge) {
      setFormData({
        id_employe: selectedConge.id_employe || "",
        mois_conge: selectedConge.mois_conge || "",
        annee_conge: selectedConge.annee_conge || "",
        jours_pris_conge: selectedConge.jours_pris_conge || "",
      });
      setMultiMonthsEnabled(false);
      setSelectedMonths([]);
      setMultiEmployeesEnabled(false);
      setSelectedEmployees([]);
      setEmployeeStatuses({});
      setSummary({ added: 0, skipped: 0, errors: 0 });
    } else {
      setFormData({
        id_employe: "",
        mois_conge: "",
        annee_conge: "2025",
        jours_pris_conge: 0,
      });
      setMultiMonthsEnabled(false);
      setSelectedMonths([]);
      setMultiEmployeesEnabled(false);
      setSelectedEmployees([]);
      setEmployeeStatuses({});
      setSummary({ added: 0, skipped: 0, errors: 0 });
    }
    setErrors({});
  }, [show, isEditMode, selectedConge]);

  useEffect(() => {
    const fetchEmployes = async () => {
      try {
        const res = await axios.get("/employes/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const options = res.data.data.map((emp) => ({
          value: emp.id_employe,
          label: `${emp.nom} ${emp.prenom}`,
        }));
        setEmployeOptions(options);
      } catch (err) {
        MySwal.fire("Erreur", "Impossible de charger les employes", "error");
      }
    };

    if (show) fetchEmployes();
  }, [show, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (multiEmployeesEnabled) {
      if (!selectedEmployees || selectedEmployees.length === 0) {
        newErrors.selected_employes = "Selectionnez au moins un employe";
      }
    } else if (!formData.id_employe) {
      newErrors.id_employe = "Employe requis";
    }

    if (isEditMode || !multiMonthsEnabled) {
      if (!formData.mois_conge) newErrors.mois_conge = "Mois requis";
    } else if (!selectedMonths || selectedMonths.length === 0) {
      newErrors.mois_conge = "Selectionnez au moins un mois";
    }

    if (!formData.annee_conge) newErrors.annee_conge = "Annee requise";
    if (formData.jours_pris_conge === "" || formData.jours_pris_conge === null) {
      newErrors.jours_pris_conge = "Champ requis";
    }
    return newErrors;
  };

  const selectStyles = useMemo(
    () => ({
      menuList: (base) => ({
        ...base,
        maxHeight: 320,
        overflowY: "auto",
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    }),
    []
  );

  const handleSelectAllEmployees = () => {
    setSelectedEmployees(employeOptions);
    setEmployeeStatuses({});
    setSummary({ added: 0, skipped: 0, errors: 0 });
  };

  const handleClearEmployees = () => {
    setSelectedEmployees([]);
    setEmployeeStatuses({});
    setSummary({ added: 0, skipped: 0, errors: 0 });
  };

  const removeEmployeeFromSelection = (empId) => {
    setSelectedEmployees((prev) => (prev || []).filter((e) => e.value !== empId));
    setEmployeeStatuses((prev) => {
      const copy = { ...prev };
      delete copy[empId];
      return copy;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const valErrors = validate();
    if (Object.keys(valErrors).length > 0) {
      setErrors(valErrors);
      return;
    }

    if (!isEditMode && multiEmployeesEnabled) {
      await handleBulkAdd();
      return;
    }

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (isEditMode) {
        const payload = { ...formData, intervalle_conge: "" };
        await axios.patch(`/conges/${selectedConge.id_conge}`, payload, config);
        MySwal.fire("Modifie", "Conge modifie avec succes", "success");
      } else if (multiMonthsEnabled) {
        const payload = {
          id_employe: formData.id_employe,
          annee_conge: formData.annee_conge,
          mois_list: selectedMonths.map((m) => m.value),
          intervalle_conge: "",
          jours_pris_conge: Number(formData.jours_pris_conge || 0),
        };
        const res = await axios.post("/conges/batch", payload, config);
        const added = res.data?.added_months?.length || 0;
        const skipped = res.data?.skipped_months?.length || 0;
        MySwal.fire("Traitement termine", `Ajoutes: ${added}\nIgnores: ${skipped}`, "success");
      } else {
        const payload = { ...formData, intervalle_conge: "" };
        await axios.post("/conges/", payload, config);
        MySwal.fire("Ajoute", "Conge ajoute avec succes", "success");
      }

      refreshData();
      handleClose();
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Erreur lors de l'enregistrement.";
      MySwal.fire("Erreur", message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAdd = async () => {
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const employeeIds = (selectedEmployees || []).map((e) => e.value);
    const initialStatuses = {};
    employeeIds.forEach((id) => {
      initialStatuses[id] = { state: "processing", message: "" };
    });
    setEmployeeStatuses(initialStatuses);
    setSummary({ added: 0, skipped: 0, errors: 0 });

    setLoading(true);
    let added = 0;
    let skipped = 0;
    let errorsCount = 0;

    try {
      if (multiMonthsEnabled) {
        const mois_list = selectedMonths.map((m) => m.value);
        for (const empId of employeeIds) {
          try {
            const payload = {
              id_employe: empId,
              annee_conge: formData.annee_conge,
              mois_list,
              intervalle_conge: "",
              jours_pris_conge: Number(formData.jours_pris_conge || 0),
            };
            const res = await axios.post("/conges/batch", payload, config);
            const addedMonths = res.data?.added_months || [];
            const skippedMonths = res.data?.skipped_months || [];
            const empState = addedMonths.length > 0 ? "success" : "skipped";
            if (empState === "success") added += 1;
            else skipped += 1;

            setEmployeeStatuses((prev) => ({
              ...prev,
              [empId]: {
                state: empState,
                message:
                  addedMonths.length > 0
                    ? `Ajoute (${addedMonths.length})`
                    : `Non ajoute (${skippedMonths.length})`,
              },
            }));
          } catch (_) {
            errorsCount += 1;
            setEmployeeStatuses((prev) => ({
              ...prev,
              [empId]: { state: "error", message: "Erreur" },
            }));
          }
        }
      } else {
        for (const empId of employeeIds) {
          try {
            const payload = { ...formData, id_employe: empId, intervalle_conge: "" };
            await axios.post("/conges/", payload, config);
            added += 1;
            setEmployeeStatuses((prev) => ({
              ...prev,
              [empId]: { state: "success", message: "Ajoute" },
            }));
          } catch (err) {
            skipped += 1;
            const msg =
              err?.response?.data?.detail || err?.response?.data?.message || "Non ajoute";
            setEmployeeStatuses((prev) => ({
              ...prev,
              [empId]: { state: "skipped", message: msg },
            }));
          }
        }
      }

      setSummary({ added, skipped, errors: errorsCount });
      refreshData();
    } finally {
      setLoading(false);
    }
  };

  const isAddDisabled = (() => {
    if (loading) return true;
    if (isEditMode) return false;
    if (multiEmployeesEnabled) {
      const noMonthSelected =
        (!multiMonthsEnabled && !formData.mois_conge) ||
        (multiMonthsEnabled && selectedMonths.length === 0);
      const noEmployees = selectedEmployees.length === 0;
      return noMonthSelected || noEmployees;
    }
    return !formData.id_employe || !formData.mois_conge;
  })();

  const selectedEmployeLabel = employeOptions.find(
    (opt) => opt.value === formData.id_employe
  )?.label;
  const selectedMonthLabel = multiMonthsEnabled
    ? selectedMonths.map((m) => m.label).join(", ")
    : formData.mois_conge || "-";

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      size="xl"
      className="rh-modern-modal rh-modal-lg conge-modal-premium"
    >
      <Modal.Header closeButton className="rh-modal-header conge-modal-header">
        <div>
          <Modal.Title>{isEditMode ? "Modifier un conge" : "Ajouter un conge"}</Modal.Title>
          <div className="rh-modal-subtitle">
            {isEditMode
              ? "Mettez a jour les informations de la ligne de conge."
              : "Saisie optimisee en mode mono ou multi-employes/mois."}
          </div>
        </div>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="rh-modal-body conge-modal-body">
          <div className="rh-modal-section conge-summary-strip">
            <div className="conge-summary-item">
              <span className="icon">
                <FaUser />
              </span>
              <div>
                <div className="label">Employe concerne</div>
                <div className="value">
                  {multiEmployeesEnabled
                    ? `${selectedEmployees.length} employe(s)`
                    : selectedEmployeLabel || "Non selectionne"}
                </div>
              </div>
            </div>
            <div className="conge-summary-item">
              <span className="icon">
                <FaCalendarAlt />
              </span>
              <div>
                <div className="label">Periode</div>
                <div className="value">
                  {selectedMonthLabel} / {formData.annee_conge || "-"}
                </div>
              </div>
            </div>
            <div className="conge-summary-item">
              <span className="icon">
                <FaLayerGroup />
              </span>
              <div>
                <div className="label">Jours pris</div>
                <div className="value">
                  {formData.jours_pris_conge === "" ? "-" : formData.jours_pris_conge}
                </div>
              </div>
            </div>
          </div>

          {!isEditMode && (
            <div className="rh-modal-section">
              <div className="rh-modal-section-title">Options avancees</div>
              <div className="conge-options-grid">
                <Form.Check
                  type="switch"
                  id="multi-employees"
                  label="Mode multi-employes"
                  checked={multiEmployeesEnabled}
                  onChange={(e) => setMultiEmployeesEnabled(e.target.checked)}
                />
                <Form.Check
                  type="switch"
                  id="multi-months"
                  label="Mode multi-mois"
                  checked={multiMonthsEnabled}
                  onChange={(e) => setMultiMonthsEnabled(e.target.checked)}
                />
              </div>
            </div>
          )}

          {!isEditMode && multiEmployeesEnabled ? (
            <Row className="g-3">
              <Col lg={7}>
                <div className="rh-modal-section h-100">
                  <div className="rh-modal-section-title d-flex justify-content-between align-items-center">
                    <span>
                      <FaUsers className="me-2" />
                      Employes cibles
                    </span>
                    <div className="conge-mini-actions">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={handleSelectAllEmployees}
                        disabled={employeOptions.length === 0}
                      >
                        Tout
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={handleClearEmployees}
                        disabled={selectedEmployees.length === 0}
                      >
                        Vider
                      </Button>
                    </div>
                  </div>

                  <Select
                    isMulti
                    options={employeOptions}
                    placeholder="Selectionner un ou plusieurs employes..."
                    value={selectedEmployees}
                    onChange={(vals) => {
                      setSelectedEmployees(vals || []);
                      setEmployeeStatuses({});
                      setSummary({ added: 0, skipped: 0, errors: 0 });
                    }}
                    className={errors.selected_employes ? "is-invalid" : ""}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                  {errors.selected_employes && (
                    <div className="invalid-feedback d-block">{errors.selected_employes}</div>
                  )}

                  <div className="conge-status-table-wrap">
                    <table className="table rh-modern-table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>Employe</th>
                          <th style={{ width: 180 }}>Statut</th>
                          <th style={{ width: 72 }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedEmployees || []).length > 0 ? (
                          selectedEmployees.map((emp) => {
                            const st = employeeStatuses[emp.value];
                            return (
                              <tr key={emp.value}>
                                <td>{emp.label}</td>
                                <td>
                                  {st?.state === "processing" && (
                                    <span className="text-muted">
                                      <Spinner animation="border" size="sm" className="me-1" />
                                      Traitement...
                                    </span>
                                  )}
                                  {st?.state === "success" && (
                                    <span className="text-success">
                                      <FaCheckCircle className="me-1" />
                                      {st?.message || "Ajoute"}
                                    </span>
                                  )}
                                  {st?.state === "skipped" && (
                                    <span className="text-warning">
                                      <FaExclamationTriangle className="me-1" />
                                      {st?.message || "Non ajoute"}
                                    </span>
                                  )}
                                  {st?.state === "error" && (
                                    <span className="text-danger">
                                      <FaTimesCircle className="me-1" />
                                      {st?.message || "Erreur"}
                                    </span>
                                  )}
                                  {!st && <span className="text-muted">-</span>}
                                </td>
                                <td>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removeEmployeeFromSelection(emp.value)}
                                    disabled={loading}
                                  >
                                    x
                                  </Button>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={3} className="text-center text-muted py-4">
                              Aucun employe selectionne
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Col>

              <Col lg={5}>
                <div className="rh-modal-section h-100">
                  <div className="rh-modal-section-title">Periode et saisie</div>

                  {!multiMonthsEnabled ? (
                    <Form.Group className="mb-3">
                      <Form.Label>Mois *</Form.Label>
                      <Select
                        options={moisOptions}
                        placeholder="Mois..."
                        value={moisOptions.find((opt) => opt.value === formData.mois_conge)}
                        onChange={(selected) => {
                          setFormData((prev) => ({ ...prev, mois_conge: selected?.value }));
                          setEmployeeStatuses({});
                          setSummary({ added: 0, skipped: 0, errors: 0 });
                        }}
                        isClearable
                        className={errors.mois_conge ? "is-invalid" : ""}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                      />
                      {errors.mois_conge && (
                        <div className="invalid-feedback d-block">{errors.mois_conge}</div>
                      )}
                    </Form.Group>
                  ) : (
                    <Form.Group className="mb-3">
                      <Form.Label>Mois (plusieurs) *</Form.Label>
                      <Select
                        isMulti
                        options={moisOptions}
                        placeholder="Selectionner un ou plusieurs mois..."
                        value={selectedMonths}
                        onChange={(vals) => {
                          setSelectedMonths(vals || []);
                          setEmployeeStatuses({});
                          setSummary({ added: 0, skipped: 0, errors: 0 });
                        }}
                        className={errors.mois_conge ? "is-invalid" : ""}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                      />
                      {errors.mois_conge && (
                        <div className="invalid-feedback d-block">{errors.mois_conge}</div>
                      )}
                    </Form.Group>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label>Annee *</Form.Label>
                    <Select
                      options={anneeOptions}
                      placeholder="Annee..."
                      value={anneeOptions.find((opt) => opt.value === formData.annee_conge)}
                      onChange={(selected) =>
                        setFormData((prev) => ({ ...prev, annee_conge: selected?.value }))
                      }
                      isClearable
                      className={errors.annee_conge ? "is-invalid" : ""}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                    {errors.annee_conge && (
                      <div className="invalid-feedback d-block">{errors.annee_conge}</div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-0">
                    <Form.Label>Jours pris *</Form.Label>
                    <Form.Control
                      type="number"
                      name="jours_pris_conge"
                      value={formData.jours_pris_conge}
                      onChange={handleChange}
                      isInvalid={!!errors.jours_pris_conge}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.jours_pris_conge}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {(summary.added + summary.skipped + summary.errors) > 0 && (
                    <div className="conge-inline-summary mt-3">
                      <span>
                        <FaCheckCircle /> Ajoutes: <strong>{summary.added}</strong>
                      </span>
                      <span>
                        <FaExclamationTriangle /> Ignores: <strong>{summary.skipped}</strong>
                      </span>
                      <span>
                        <FaTimesCircle /> Erreurs: <strong>{summary.errors}</strong>
                      </span>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          ) : (
            <Row className="g-3">
              <Col lg={7}>
                <div className="rh-modal-section h-100">
                  <div className="rh-modal-section-title">Employe concerne</div>
                  {multiEmployeesEnabled ? (
                    <>
                      <Select
                        isMulti
                        options={employeOptions}
                        placeholder="Selectionner un ou plusieurs employes..."
                        value={selectedEmployees}
                        onChange={(vals) => setSelectedEmployees(vals || [])}
                        className={errors.selected_employes ? "is-invalid" : ""}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                      />
                      {errors.selected_employes && (
                        <div className="invalid-feedback d-block">{errors.selected_employes}</div>
                      )}
                    </>
                  ) : (
                    <>
                      <Select
                        options={employeOptions}
                        placeholder="Selectionner un employe..."
                        value={employeOptions.find((opt) => opt.value === formData.id_employe)}
                        onChange={(selected) =>
                          setFormData((prev) => ({ ...prev, id_employe: selected?.value }))
                        }
                        isClearable
                        className={errors.id_employe ? "is-invalid" : ""}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                      />
                      {errors.id_employe && (
                        <div className="invalid-feedback d-block">{errors.id_employe}</div>
                      )}
                    </>
                  )}

                  {!isEditMode && multiEmployeesEnabled && selectedEmployees.length > 0 && (
                    <div className="conge-chip-list mt-3">
                      {selectedEmployees.map((e) => (
                        <span className="conge-chip" key={e.value}>
                          {e.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Col>

              <Col lg={5}>
                <div className="rh-modal-section h-100">
                  <div className="rh-modal-section-title">Periode de conge</div>

                  {!multiMonthsEnabled ? (
                    <Form.Group className="mb-3">
                      <Form.Label>Mois *</Form.Label>
                      <Select
                        options={moisOptions}
                        placeholder="Mois..."
                        value={moisOptions.find((opt) => opt.value === formData.mois_conge)}
                        onChange={(selected) =>
                          setFormData((prev) => ({ ...prev, mois_conge: selected?.value }))
                        }
                        isClearable
                        className={errors.mois_conge ? "is-invalid" : ""}
                        isDisabled={isEditMode}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                      />
                      {errors.mois_conge && (
                        <div className="invalid-feedback d-block">{errors.mois_conge}</div>
                      )}
                    </Form.Group>
                  ) : (
                    <Form.Group className="mb-3">
                      <Form.Label>Mois (plusieurs) *</Form.Label>
                      <Select
                        isMulti
                        options={moisOptions}
                        placeholder="Selectionner un ou plusieurs mois..."
                        value={selectedMonths}
                        onChange={(vals) => setSelectedMonths(vals || [])}
                        className={errors.mois_conge ? "is-invalid" : ""}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                      />
                      {errors.mois_conge && (
                        <div className="invalid-feedback d-block">{errors.mois_conge}</div>
                      )}
                    </Form.Group>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label>Annee *</Form.Label>
                    <Select
                      options={anneeOptions}
                      placeholder="Annee..."
                      value={anneeOptions.find((opt) => opt.value === formData.annee_conge)}
                      onChange={(selected) =>
                        setFormData((prev) => ({ ...prev, annee_conge: selected?.value }))
                      }
                      isClearable
                      className={errors.annee_conge ? "is-invalid" : ""}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                    {errors.annee_conge && (
                      <div className="invalid-feedback d-block">{errors.annee_conge}</div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-0">
                    <Form.Label>Jours pris *</Form.Label>
                    <Form.Control
                      type="number"
                      name="jours_pris_conge"
                      value={formData.jours_pris_conge}
                      onChange={handleChange}
                      isInvalid={!!errors.jours_pris_conge}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.jours_pris_conge}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>

        <Modal.Footer className="rh-modal-footer conge-modal-footer-sticky">
          <Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={isAddDisabled}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Traitement...
              </>
            ) : isEditMode ? (
              "Enregistrer les modifications"
            ) : (
              "Enregistrer le conge"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CongeFormModal;
